/**
 * Where an unauthenticated portal visitor goes, and where login sends them back.
 *
 * WHY THIS IS ONE MODULE. Before 2026-09-21 `/portal` had no answer for "no
 * session": `AdminRedirectGate` handled the admin-token case and let everything
 * else fall through to the dashboard, which 401-stormed and then rendered a bare
 * line of text — "Unable to load your portal. Please log in again." — with no
 * link and no redirect. Two sites now have to agree on the same vocabulary: the
 * gate that BUILDS the login href, and the login page that CONSUMES its `next`.
 * Splitting them is how a `next` the gate emits becomes one the login page
 * silently drops.
 *
 * Both functions are deliberately paranoid about `next`, because it is a
 * redirect target that arrives on the URL:
 *   - it must be a root-relative path (no scheme, no `//host`, no `\\host`),
 *   - it must land inside `/portal`, so the portal login cannot be used as an
 *     open redirect into the admin app or off-origin,
 *   - it must NOT be `/portal/login` itself, which is the redirect loop the
 *     product owner would hit next,
 *   - and it must contain no `..` segment, which is how `/portal/../../login`
 *     escapes the prefix check while still passing `startsWith('/portal')`.
 * Anything else resolves to the canonical post-login landing rather than
 * failing — a visitor with a mangled link still gets into their portal.
 */

/** The employee portal's own login route (`app/(portal)/portal/login/page.tsx`). */
export const PORTAL_LOGIN_PATH = '/portal/login';

/**
 * Where login lands a recipient when no usable `next` came along. This is the
 * pre-existing destination of the OTP flow and is kept byte-for-byte so adding
 * `next` support changes nothing for a visitor who arrived without one.
 */
export const PORTAL_POST_LOGIN_DEFAULT = '/portal/training';

/** `/portal`, `/portal/…`, `/portal?…` — but never `/portal-settings`. */
const PORTAL_ROUTE = /^\/portal(?:[/?#]|$)/;

/**
 * `?ws=` is a workspace UUID and nothing else.
 *
 * VERIFIED IN A BROWSER, 2026-09-21, on the build this fix has not touched:
 * `/portal/login?ws=ws-demo` never renders the sign-in form. The (portal)
 * layout gates its children on `!!workspaceId && settingsLoading`, the public
 * settings call 400s on an id that is not a uuid, react-query keeps retrying,
 * and the visitor sits on "Loading portal…" for as long as they care to wait.
 * That is a pre-existing layout defect — but this redirect is what would now
 * DELIVER people into it, so a `ws` we cannot vouch for is not forwarded.
 */
const WORKSPACE_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Is `candidate` a root-relative path inside the employee portal that is safe
 * to hand to `router.push` / `router.replace`?
 */
/**
 * The sessionStorage key the `(portal)` layout resolves its workspace from
 * (`layout.tsx` WS_STORAGE_KEY). Declared here so the gate can purge a value
 * the shell cannot resolve without importing the layout.
 */
export const PORTAL_WS_STORAGE_KEY = 'hf-portal:ws-id';

/** Is this a workspace id the portal shell can actually resolve? */
export function isResolvableWorkspaceId(value: string | null | undefined): boolean {
  return typeof value === 'string' && WORKSPACE_ID.test(value);
}

export function isSafePortalPath(candidate: string): boolean {
  if (typeof candidate !== 'string' || candidate.length === 0) return false;
  if (!candidate.startsWith('/')) return false;
  // `//evil.example` is protocol-relative — the browser treats it as off-origin.
  if (candidate.startsWith('//')) return false;
  // A backslash is normalised to `/` by browsers, so `/\evil.example` is the
  // same attack wearing a different hat.
  if (candidate.includes('\\')) return false;
  // Whitespace / control characters are never legitimate here and are the
  // classic way to smuggle a scheme past a prefix check.
  if (/[\s\u0000-\u001f\u007f]/.test(candidate)) return false;
  if (!PORTAL_ROUTE.test(candidate)) return false;

  const pathOnly = candidate.split(/[?#]/)[0];
  // `/portal/../../login` starts with `/portal` and resolves somewhere else.
  if (pathOnly.split('/').includes('..')) return false;
  // The loop: login must never send anyone back to login.
  if (pathOnly === PORTAL_LOGIN_PATH || pathOnly.startsWith(`${PORTAL_LOGIN_PATH}/`)) {
    return false;
  }
  return true;
}

/** Strip a leading `?` so callers can pass either shape. */
function normalizeSearch(search: string | null | undefined): string {
  if (!search) return '';
  return search.startsWith('?') ? search.slice(1) : search;
}

/**
 * The login href for a visitor who is standing on `pathname?search` without a
 * live portal session.
 *
 * `ws` is forwarded at the TOP level as well as inside `next`, because the login
 * page and the portal layout both theme off `?ws=` (`useWorkspaceId`) — without
 * it a deep link that carried a workspace would arrive at an unthemed login.
 */
export function buildPortalLoginHref(
  pathname: string | null | undefined,
  search?: string | null,
): string {
  const incoming = new URLSearchParams(normalizeSearch(search));
  const ws = incoming.get('ws');
  const usableWs = ws && WORKSPACE_ID.test(ws) ? ws : null;
  // A ws we will not forward is also stripped from `next`: the portal layout
  // prefers the QUERY param over storage, so carrying it through would poison
  // the very page the visitor lands on once they have signed in.
  if (ws && !usableWs) incoming.delete('ws');

  const params = new URLSearchParams();
  if (usableWs) params.set('ws', usableWs);

  const currentSearch = incoming.toString();
  const target = `${pathname ?? ''}${currentSearch ? `?${currentSearch}` : ''}`;
  if (isSafePortalPath(target)) params.set('next', target);

  const qs = params.toString();
  return qs ? `${PORTAL_LOGIN_PATH}?${qs}` : PORTAL_LOGIN_PATH;
}

/**
 * The path the login page should navigate to after a successful sign-in.
 * Returns `PORTAL_POST_LOGIN_DEFAULT` for anything it will not vouch for.
 */
export function resolvePortalNextPath(raw: string | null | undefined): string {
  if (typeof raw !== 'string') return PORTAL_POST_LOGIN_DEFAULT;
  return isSafePortalPath(raw) ? raw : PORTAL_POST_LOGIN_DEFAULT;
}

/**
 * Is this portal access token unusable WITHOUT asking the server?
 *
 * WHY THIS EXISTS. `AdminRedirectGate` used to decide on the mere PRESENCE of
 * `hf-portal:token`, so a token that was missing, malformed or long expired
 * still let `PortalDashboardInner` mount — and its ~14 hooks then fired a
 * 401-storm before anything could redirect. Measured in a browser on
 * 2026-09-21: a dead token produced 18 unauthorized requests to /v1/portal/*
 * before the recovery landed, which is precisely the storm the gate's own
 * comment says it exists to prevent.
 *
 * A JWT carries its own expiry, so the common cases — no token, not a JWT,
 * already expired — are answerable locally, with no round trip and no storm.
 *
 * DELIBERATELY NOT A SECURITY CHECK. The signature is not verified here and
 * must never be: the server is the only authority on whether a token is valid.
 * This only decides whether it is worth SENDING. A token that is well-formed
 * and unexpired but revoked server-side still reaches the API, 401s, and is
 * recovered by the session-recovery effect in the dashboard — one storm in the
 * rare case rather than one in the common case.
 *
 * Fail OPEN: anything this cannot parse confidently is treated as usable, so a
 * future token format cannot lock every employee out of the portal. The cost of
 * a false "usable" is one redirect later; the cost of a false "dead" is a
 * sign-in loop for everyone.
 */
export function isPortalTokenDeadLocally(token: string | null | undefined): boolean {
  if (typeof token !== 'string' || token.trim() === '') return true;

  const segments = token.split('.');
  if (segments.length !== 3) return true; // not a JWT at all

  let payload: unknown;
  try {
    const raw = segments[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = raw + '='.repeat((4 - (raw.length % 4)) % 4);
    payload = JSON.parse(
      decodeURIComponent(
        atob(padded)
          .split('')
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join(''),
      ),
    );
  } catch {
    return true; // three segments but the middle one is not a JWT payload
  }

  if (typeof payload !== 'object' || payload === null) return true;
  const exp = (payload as { exp?: unknown }).exp;
  // No `exp` claim: not something we will call dead — fail open.
  if (typeof exp !== 'number' || !Number.isFinite(exp)) return false;
  // 30s of slack absorbs clock skew between this browser and the API, so a
  // token seconds from expiry is not thrown away on a fast machine.
  return exp * 1000 <= Date.now() + 30_000;
}
